// ─── Kalender, Feiertage, Schulplan, Urlaub ──────────────────────────────────
// Reine Funktionen auf ISO-Datumsstrings (YYYY-MM-DD). Kein Date-Objekt verlässt
// das Modul — damit gibt es keine Zeitzonen-Verschiebungen um Mitternacht.

const pad = (n) => String(n).padStart(2, '0');

export function iso(jahr, monat, tag) {
    return `${jahr}-${pad(monat)}-${pad(tag)}`;
}

function alsUtc(datum) {
    const [j, m, t] = String(datum).slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(j, m - 1, t));
}

function ausUtc(d) {
    return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function plusTage(datum, tage) {
    const d = alsUtc(datum);
    d.setUTCDate(d.getUTCDate() + tage);
    return ausUtc(d);
}

/** 1 = Montag … 7 = Sonntag (ISO) */
export function wochentag(datum) {
    const w = alsUtc(datum).getUTCDay();
    return w === 0 ? 7 : w;
}

/** ISO-Kalenderwoche */
export function kalenderwoche(datum) {
    const d = alsUtc(datum);
    const tag = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - tag + 3);
    const ersterDonnerstag = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    return 1 + Math.round(((d - ersterDonnerstag) / 86400000 - 3 + ((ersterDonnerstag.getUTCDay() + 6) % 7)) / 7);
}

/** Ganze Wochen zwischen den Montagen zweier Daten (für 14-tägige Rhythmen). */
export function wochenAbstand(a, b) {
    const montag = (x) => plusTage(x, 1 - wochentag(x));
    return Math.round((alsUtc(montag(b)) - alsUtc(montag(a))) / (7 * 86400000));
}

export function tageZwischen(von, bis) {
    const tage = [];
    for (let d = String(von).slice(0, 10); d <= String(bis).slice(0, 10); d = plusTage(d, 1)) tage.push(d);
    return tage;
}

// Ostersonntag nach der Gaußschen Osterformel (Anonymer Gregorianischer Algorithmus)
export function ostersonntag(jahr) {
    const a = jahr % 19;
    const b = Math.floor(jahr / 100);
    const c = jahr % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const monat = Math.floor((h + l - 7 * m + 114) / 31);
    const tag = ((h + l - 7 * m + 114) % 31) + 1;
    return iso(jahr, monat, tag);
}

export const BUNDESLAENDER = {
    BW: 'Baden-Württemberg', BY: 'Bayern', BE: 'Berlin', BB: 'Brandenburg', HB: 'Bremen', HH: 'Hamburg',
    HE: 'Hessen', MV: 'Mecklenburg-Vorpommern', NI: 'Niedersachsen', NW: 'Nordrhein-Westfalen',
    RP: 'Rheinland-Pfalz', SL: 'Saarland', SN: 'Sachsen', ST: 'Sachsen-Anhalt', SH: 'Schleswig-Holstein', TH: 'Thüringen',
};

/** Buß- und Bettag: Mittwoch vor dem 23. November */
function bussUndBettag(jahr) {
    let d = iso(jahr, 11, 22);
    while (wochentag(d) !== 3) d = plusTage(d, -1);
    return d;
}

/**
 * Gesetzliche Feiertage eines Bundeslands — berechnet, nicht gepflegt.
 * @returns [{ datum, name }] sortiert
 */
export function feiertage(bundesland, jahr) {
    const bl = String(bundesland || 'SH').toUpperCase();
    const ostern = ostersonntag(jahr);
    const liste = [
        [iso(jahr, 1, 1), 'Neujahr'],
        [plusTage(ostern, -2), 'Karfreitag'],
        [plusTage(ostern, 1), 'Ostermontag'],
        [iso(jahr, 5, 1), 'Tag der Arbeit'],
        [plusTage(ostern, 39), 'Christi Himmelfahrt'],
        [plusTage(ostern, 50), 'Pfingstmontag'],
        [iso(jahr, 10, 3), 'Tag der Deutschen Einheit'],
        [iso(jahr, 12, 25), '1. Weihnachtstag'],
        [iso(jahr, 12, 26), '2. Weihnachtstag'],
    ];
    if (['BW', 'BY', 'ST'].includes(bl)) liste.push([iso(jahr, 1, 6), 'Heilige Drei Könige']);
    if (['BE', 'MV'].includes(bl)) liste.push([iso(jahr, 3, 8), 'Internationaler Frauentag']);
    if (bl === 'BB') {
        liste.push([ostern, 'Ostersonntag']);
        liste.push([plusTage(ostern, 49), 'Pfingstsonntag']);
    }
    if (['BW', 'BY', 'HE', 'NW', 'RP', 'SL'].includes(bl)) liste.push([plusTage(ostern, 60), 'Fronleichnam']);
    if (['SL'].includes(bl)) liste.push([iso(jahr, 8, 15), 'Mariä Himmelfahrt']);
    if (bl === 'TH') liste.push([iso(jahr, 9, 20), 'Weltkindertag']);
    if (['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'].includes(bl)) liste.push([iso(jahr, 10, 31), 'Reformationstag']);
    if (['BW', 'BY', 'NW', 'RP', 'SL'].includes(bl)) liste.push([iso(jahr, 11, 1), 'Allerheiligen']);
    if (bl === 'SN') liste.push([bussUndBettag(jahr), 'Buß- und Bettag']);
    return liste.map(([datum, name]) => ({ datum, name })).sort((a, b) => (a.datum < b.datum ? -1 : 1));
}

export function feiertagsMap(bundesland, von, bis) {
    const map = new Map();
    const j1 = Number(String(von).slice(0, 4));
    const j2 = Number(String(bis).slice(0, 4));
    for (let j = j1; j <= j2; j++) for (const f of feiertage(bundesland, j)) map.set(f.datum, f.name);
    return map;
}

/**
 * Arbeitstage im Zeitraum: Tage des Arbeitszeitmodells (Default Mo–Fr) ohne Feiertage.
 * betrieblicheTage: [{ datum, anteil }] — z. B. Heiligabend 0.5 frei.
 */
export function arbeitstage(von, bis, { bundesland = 'SH', wochentage = [1, 2, 3, 4, 5], betrieblicheTage = [] } = {}) {
    const ft = feiertagsMap(bundesland, von, bis);
    const frei = new Map(betrieblicheTage.map((t) => [t.datum, Number(t.anteil ?? 1)]));
    let summe = 0;
    const tage = [];
    for (const d of tageZwischen(von, bis)) {
        if (!wochentage.includes(wochentag(d)) || ft.has(d)) continue;
        const anteil = 1 - (frei.get(d) || 0);
        if (anteil <= 0) continue;
        summe += anteil;
        tage.push({ datum: d, anteil });
    }
    return { summe, tage };
}

/**
 * Berufsschultage aus Schulplänen. Pläne:
 *   { typ: 'wochenmuster', wochentage: [3], rhythmus: 'woechentlich'|'14taegig'|'a_woche'|'b_woche',
 *     a_woche_ab, umfang: 'ganztags'|'vormittag'|'nachmittag', von, bis, stunden }
 *   { typ: 'block', von, bis, stunden }            — stunden je Woche
 *   { typ: 'ausnahme', von, bis }                  — kein Unterricht (Ferien, Prüfungen)
 *   { typ: 'ueberbetrieblich', von, bis, notiz }
 * a_woche_ab: irgendein Tag einer A-Woche; ohne Angabe gilt die Woche von `von` als A-Woche.
 * @returns [{ datum, art: 'berufsschule'|'ueberbetrieblich', anrechnung: 'tag'|'woche'|'stunden', stunden, umfang }]
 */
export function schultage(plaene, von, bis, { bundesland = 'SH' } = {}) {
    const ft = feiertagsMap(bundesland, von, bis);
    const ausnahmen = (plaene || []).filter((p) => p.typ === 'ausnahme');
    const istAusnahme = (d) => ausnahmen.some((a) => d >= a.von && d <= a.bis);
    const ergebnis = new Map();

    for (const p of plaene || []) {
        if (p.typ === 'ausnahme') continue;
        const start = p.von > von ? p.von : von;
        const ende = p.bis && p.bis < bis ? p.bis : bis;
        if (start > ende) continue;
        for (const d of tageZwischen(start, ende)) {
            const wt = wochentag(d);
            if (wt > 5 || ft.has(d) || istAusnahme(d)) continue;
            if (p.typ === 'wochenmuster') {
                if (!(p.wochentage || []).includes(wt)) continue;
                if (p.rhythmus === '14taegig' && wochenAbstand(p.von, d) % 2 !== 0) continue;
                if (p.rhythmus === 'a_woche' || p.rhythmus === 'b_woche') {
                    const aWoche = Math.abs(wochenAbstand(p.a_woche_ab || p.von, d)) % 2 === 0;
                    if (aWoche !== (p.rhythmus === 'a_woche')) continue;
                }
                // Mehr als 5 Unterrichtsstunden à 45 min: Anrechnung als voller Arbeitstag (§ 15 BBiG)
                const stunden = Number(p.stunden || 0);
                const umfang = p.umfang || 'ganztags';
                // Ganzer Tag schlägt halben, wenn sich zwei Einträge überschneiden
                if (umfang !== 'ganztags' && ergebnis.get(d)?.umfang === 'ganztags') continue;
                ergebnis.set(d, { datum: d, art: 'berufsschule', anrechnung: stunden > 5 ? 'tag' : 'stunden', stunden, umfang });
            } else if (p.typ === 'block') {
                // Blockwoche mit mind. 25 Stunden an mind. 5 Tagen: Anrechnung der Wochenarbeitszeit
                ergebnis.set(d, { datum: d, art: 'berufsschule', anrechnung: Number(p.stunden || 0) >= 25 ? 'woche' : 'stunden', stunden: Number(p.stunden || 0) / 5, umfang: 'ganztags' });
            } else if (p.typ === 'ueberbetrieblich') {
                ergebnis.set(d, { datum: d, art: 'ueberbetrieblich', anrechnung: 'tag', stunden: null, notiz: p.notiz || '', umfang: 'ganztags' });
            }
        }
    }
    return [...ergebnis.values()].sort((a, b) => (a.datum < b.datum ? -1 : 1));
}

/** Urlaubstage eines Antrags: Arbeitstage im Zeitraum (halbe Tage zählen 0,5). */
export function urlaubstage(von, bis, { bundesland = 'SH', wochentage = [1, 2, 3, 4, 5], betrieblicheTage = [], umfang = 'ganztags' } = {}) {
    const { summe } = arbeitstage(von, bis, { bundesland, wochentage, betrieblicheTage });
    return umfang === 'ganztags' ? summe : Math.min(summe, 0.5);
}

/**
 * Anteiliger Urlaubsanspruch im Ein-/Austrittsjahr: 1/12 je vollem Monat,
 * Bruchteile ab 0,5 Tag aufrunden (§ 5 BUrlG).
 */
export function anteiligerUrlaub(anspruchJahr, jahr, { eintritt, austritt } = {}) {
    const start = eintritt && eintritt > iso(jahr, 1, 1) ? eintritt : iso(jahr, 1, 1);
    const ende = austritt && austritt < iso(jahr, 12, 31) ? austritt : iso(jahr, 12, 31);
    if (start > ende) return 0;
    let monate = 0;
    for (let m = 1; m <= 12; m++) {
        const erster = iso(jahr, m, 1);
        const letzter = plusTage(m === 12 ? iso(jahr + 1, 1, 1) : iso(jahr, m + 1, 1), -1);
        if (start <= erster && ende >= letzter) monate += 1;
    }
    const roh = (anspruchJahr * monate) / 12;
    const ganz = Math.floor(roh);
    return roh - ganz >= 0.5 ? ganz + 1 : ganz;
}

/** Ist jemand am Stichtag minderjährig? (Jugendarbeitsschutz) */
export function istMinderjaehrig(geburtsdatum, stichtag) {
    if (!geburtsdatum) return false;
    const g = String(geburtsdatum).slice(0, 10);
    const achtzehnterGeburtstag = `${Number(g.slice(0, 4)) + 18}${g.slice(4)}`;
    return achtzehnterGeburtstag > String(stichtag).slice(0, 10);
}
